import {
  AssociationType,
  type AssociationRecord,
} from "@/shared/domain/association";
import type { CreateEventRequest } from "@/shared/domain/events/EventRecord";
import { legacyEventsWritable } from "@/shared/domain/orgs";

import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
} from "./ClientTools";
import { CreateEventsConfirmation } from "./CreateEventsConfirmation";
import {
  commonEventFields,
  createEachEvent,
  EVENT_COMMON_PROPERTIES,
  type EventInputCommon,
} from "./eventCreation";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

interface EventAssociationInput {
  dataset_id?: string;
  file_id?: string;
  topic_id?: string;
  message_path_id?: string;
}

interface EventInput extends EventInputCommon {
  /** The data the event is about. */
  associations: EventAssociationInput[];
}

export interface CreateEventsInput {
  events: EventInput[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert the LLM's association input into the `AssociationRecord` shape
 * expected by the backend.
 */
function toAssociationRecord(input: EventAssociationInput): AssociationRecord {
  if (input.dataset_id) {
    return {
      association_id: input.dataset_id,
      association_type: AssociationType.Dataset,
    };
  }
  if (input.file_id) {
    return {
      association_id: input.file_id,
      association_type: AssociationType.File,
    };
  }
  if (input.topic_id) {
    return {
      association_id: input.topic_id,
      association_type: AssociationType.Topic,
    };
  }
  if (input.message_path_id) {
    return {
      association_id: input.message_path_id,
      association_type: AssociationType.MessagePath,
    };
  }
  throw new Error("Association must have at least one ID field set");
}

/**
 * The request that creates `event` as a dataset event.
 *
 * @throws Error when the event names no associations, or when the org's
 * event model is known and its dataset events cannot be created.
 */
function toCreateEventRequest(
  event: EventInput,
  deps: ClientToolDeps,
): CreateEventRequest {
  if ((event.associations?.length ?? 0) === 0) {
    throw new Error("associations must name at least one record");
  }
  if (deps.eventModel !== undefined && !legacyEventsWritable(deps.eventModel)) {
    throw new Error(
      "This organization's dataset events cannot be created; use client_create_session_events",
    );
  }
  return {
    ...commonEventFields(event),
    associations: event.associations.map(toAssociationRecord),
  };
}

// ---------------------------------------------------------------------------
// Tool spec
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    events: {
      type: "array",
      description: "Array of events to create.",
      items: {
        type: "object",
        properties: {
          ...EVENT_COMMON_PROPERTIES,
          associations: {
            type: "array",
            description:
              "The data entities the event is associated with: datasets, files, topics or message paths.",
            items: {
              type: "object",
              properties: {
                dataset_id: { type: "string" },
                file_id: { type: "string" },
                topic_id: { type: "string" },
                message_path_id: { type: "string" },
              },
            },
          },
        },
        required: ["name", "start_time", "end_time", "associations"],
      },
    },
  },
  required: ["events"],
};

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: CreateEventsInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  return createEachEvent(input.events, deps, toCreateEventRequest);
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<CreateEventsInput>({
  name: "client_create_events",
  description:
    "Create one or more time-based event annotations on datasets. " +
    "Events mark interesting time ranges (e.g., sensor anomalies, system events, " +
    "behavioral patterns); each is associated with the datasets, files, topics or " +
    "message paths it is about. " +
    "Timestamps are in nanoseconds since Unix epoch.",
  input_schema: inputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.REQUIRE_APPROVAL,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Create Dataset Events",
  displayDescription: "Creates time-based event annotations on datasets.",
  ConfirmationContent: CreateEventsConfirmation,
});
